import { useEffect, useRef } from 'react'

/**
 * Reveals an element once it scrolls into view.
 *
 * Elements start at `.reveal` and gain `.is-visible` on first intersection —
 * the observer detaches immediately after, so nothing re-animates on the way
 * back up. Reduced-motion users are marked visible straight away.
 */
export const useReveal = (options = {}) => {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced || typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-visible')
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('is-visible')
          observer.unobserve(entry.target)
        })
      },
      { rootMargin: '0px 0px -40px 0px', threshold: 0.05, ...options }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [options])

  return ref
}

/** Convenience wrapper: a revealing block with an optional stagger delay. */
export const Reveal = ({ as: Tag = 'div', delay = 0, className = '', style, ...rest }) => {
  const ref = useReveal()
  // Merge rather than replace: callers pass their own custom properties too.
  const merged = delay ? { '--reveal-delay': `${delay}ms`, ...style } : style
  return (
    <Tag
      ref={ref}
      className={`reveal ${className}`.trim()}
      style={merged}
      {...rest}
    />
  )
}
